import {getAdjustedCdnUrl} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {mediaTabletAndUp} from '../../../constants/customMedia';
import {FONT_SIZES} from '../../../constants/typography';
import type {ImageType} from '../../../types/Image';
import {Image} from '../../atoms/Image/Image';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';
import {AutoScrollSlider} from '../../molecules/AutoScrollSlider/AutoScrollSlider';
import {HorizontalListSlider} from '../../molecules/HorizontalList/HorizontalList';

export type UspListType = 'primary' | 'secondary';

export interface AutoScrollProps {
  itemsPerSlide?: number;
  durationPerItemInMs: number;
  testId: string;
  type: UspListType;
  className?: string;
}

export interface UspProps {
  items: {
    label: string;
    href?: string;
    image: ImageType;
  }[];
}

const backgroundTypeMap = {
  primary: COLORS.Grey3,
  secondary: COLORS.Black,
};

export const UspList: React.FC<UspProps & AutoScrollProps> = ({
  itemsPerSlide,
  durationPerItemInMs,
  testId,
  type,
  items,
  className,
}) => {
  return (
    <StyledAutoScrollSlider
      className={className}
      $itemsPerSlide={itemsPerSlide}
      $type={type}
      durationPerItemInMs={durationPerItemInMs}
      testId={testId}
      disableFade
    >
      {items.map((item, index) => {
        const content = (
          <React.Fragment key={index}>
            <StyledImage
              src={getAdjustedCdnUrl(item.image.url, {
                bg: (type === 'secondary'
                  ? COLORS.White
                  : backgroundTypeMap[type]
                ).replace('#', ''),
              })}
              alt={item.image.description}
              breakpoints={[]}
              widths={[75]}
              ratio={'1:1'}
              loading="lazy"
              importance="low"
              $invert={type === 'secondary'}
            />
            <StyledText>{item.label}</StyledText>
          </React.Fragment>
        );

        return item.href ? (
          <UspTileLink
            key={index}
            to={item.href}
            $itemsPerSlide={itemsPerSlide}
            className="uspListItem"
            aria-label={undefined}
          >
            {content}
          </UspTileLink>
        ) : (
          <UspTile
            key={index}
            $itemsPerSlide={itemsPerSlide}
            className="uspListItem"
          >
            {content}
          </UspTile>
        );
      })}
    </StyledAutoScrollSlider>
  );
};

const StyledText = styled.span`
  font-weight: bold;
`;

const StyledAutoScrollSlider = styled(AutoScrollSlider)<{
  $type: UspListType;
  $itemsPerSlide?: number;
}>`
  display: flex;

  ${HorizontalListSlider} {
    background-color: ${props => backgroundTypeMap[props.$type]};
    color: ${props =>
      props.$type === 'secondary' ? COLORS.White : COLORS.Black};

    @media ${mediaTabletAndUp} {
      justify-content: ${({$itemsPerSlide}) =>
        $itemsPerSlide === undefined ? 'center' : ''};
    }
  }
`;

const uspTileStyles = `
  display: flex;
  padding: 18px;
  align-items: center;
  box-sizing: border-box;
  text-transform: uppercase;
  font-size: ${FONT_SIZES.px10};
  justify-content: center;
  scroll-snap-align: start;
`;

const UspTile = styled.div<{$itemsPerSlide?: number}>`
  ${uspTileStyles}

  width: ${props => (props.$itemsPerSlide ? '0' : 'unset')};
  flex-shrink: ${props => (props.$itemsPerSlide ? '0' : '1')};
  flex-basis: ${props =>
    props.$itemsPerSlide ? `calc(100% / ${props.$itemsPerSlide})` : 'auto'};
`;

const UspTileLink = styled(UnstyledLinkLinaria)<{
  $itemsPerSlide?: number;
}>`
  ${uspTileStyles}

  width: ${props => (props.$itemsPerSlide ? '0' : 'unset')};
  flex-shrink: ${props => (props.$itemsPerSlide ? '0' : '1')};
  flex-basis: ${props =>
    props.$itemsPerSlide ? `calc(100% / ${props.$itemsPerSlide})` : 'auto'};
`;

const StyledImage = styled(Image)<{$invert: boolean}>`
  width: 30px;
  height: 30px;
  margin-right: 1rem;
  margin-left: 1rem;
  filter: ${props => (props.$invert ? 'invert(1) opacity(1)' : 'none')};
`;
